/**
 * OverviewTab - Key metrics grid with recent activity highlights
 */
import { MetricCard } from './MetricCard'
import { FilingCard } from './FilingCard'
import { TradeRow } from './TradeRow'
import {
  useCompanyMetrics,
  useSecFilings,
  useInsiderSummary,
  useInstitutionalSummary,
} from '../../hooks/useResearch'

interface OverviewTabProps {
  symbol: string
}

// Skeleton component for loading states
function MetricCardSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
      <div className="h-8 bg-gray-200 rounded w-24" />
    </div>
  )
}

function SectionSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-16 bg-gray-100 rounded-lg" />
      ))}
    </div>
  )
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--'
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000_000_000) {
    return `$${(value / 1_000_000_000_000).toFixed(1)}T`
  }
  if (absValue >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`
  }
  if (absValue >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`
  }
  return `$${value.toLocaleString()}`
}

export function OverviewTab({ symbol }: OverviewTabProps) {
  const { data: metrics, isLoading: metricsLoading, error: metricsError } = useCompanyMetrics(symbol)
  const { data: filings, isLoading: filingsLoading } = useSecFilings(symbol, { limit: 3 })
  const { data: insiderSummary, isLoading: insiderLoading } = useInsiderSummary(symbol, 90)
  const { data: institutionalSummary, isLoading: institutionalLoading } = useInstitutionalSummary(symbol)

  return (
    <div className="space-y-8">
      {/* Key Metrics Grid */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h3>

        {metricsError && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">
            No fundamentals data available for this symbol.
          </div>
        )}

        {metricsLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <MetricCardSkeleton key={i} />
            ))}
          </div>
        ) : metrics ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <MetricCard
              label="Market Cap"
              value={metrics.market_cap}
              format="currency"
              size="sm"
            />
            <MetricCard
              label="P/E Ratio"
              value={metrics.price_to_earnings}
              format="ratio"
              size="sm"
            />
            <MetricCard
              label="Revenue Growth"
              value={metrics.revenue_growth}
              format="percent"
              size="sm"
            />
            <MetricCard
              label="Net Margin"
              value={metrics.net_margin}
              format="percent"
              size="sm"
            />
            <MetricCard
              label="EPS"
              value={metrics.earnings_per_share}
              format="currency"
              size="sm"
            />
            <MetricCard
              label="Dividend Yield"
              value={metrics.dividend_yield}
              format="percent"
              size="sm"
            />
          </div>
        ) : null}
      </section>

      {/* Recent Activity Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Latest Filing */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Latest Filing</h3>
          {filingsLoading ? (
            <SectionSkeleton lines={1} />
          ) : filings?.filings && filings.filings.length > 0 ? (
            <FilingCard filing={filings.filings[0]} />
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
              No filings available
            </div>
          )}
        </section>

        {/* Insider Activity Summary */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Insider Activity (90 days)
          </h3>
          {insiderLoading ? (
            <SectionSkeleton lines={2} />
          ) : insiderSummary ? (
            <div className="space-y-4">
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 font-medium">Net Shares</p>
                  <p
                    className={`text-lg font-semibold ${
                      insiderSummary.net_shares > 0
                        ? 'text-green-600'
                        : insiderSummary.net_shares < 0
                          ? 'text-red-600'
                          : 'text-gray-900'
                    }`}
                  >
                    {insiderSummary.net_shares > 0 ? '+' : ''}
                    {insiderSummary.net_shares.toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 font-medium">Buys</p>
                  <p className="text-lg font-semibold text-green-600">
                    {insiderSummary.buy_count}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 font-medium">Sells</p>
                  <p className="text-lg font-semibold text-red-600">
                    {insiderSummary.sell_count}
                  </p>
                </div>
              </div>

              {/* Most recent trade */}
              {insiderSummary.most_recent_trade && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Most Recent Trade</p>
                  <TradeRow trade={insiderSummary.most_recent_trade} />
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
              No insider activity
            </div>
          )}
        </section>
      </div>

      {/* Institutional Holdings Summary */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Top Institutional Holders
        </h3>
        {institutionalLoading ? (
          <SectionSkeleton lines={3} />
        ) : institutionalSummary ? (
          <div className="space-y-4">
            {/* Summary stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium">Total Holders</p>
                <p className="text-xl font-semibold text-gray-900">
                  {institutionalSummary.total_institutions}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium">Shares Held</p>
                <p className="text-xl font-semibold text-gray-900">
                  {institutionalSummary.total_shares >= 1_000_000
                    ? `${(institutionalSummary.total_shares / 1_000_000).toFixed(1)}M`
                    : institutionalSummary.total_shares.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium">Market Value</p>
                <p className="text-xl font-semibold text-gray-900">
                  {formatCurrency(institutionalSummary.total_market_value)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium">New Positions</p>
                <p className="text-xl font-semibold text-green-600">
                  {institutionalSummary.new_positions}
                </p>
              </div>
            </div>

            {/* Top holders list */}
            {institutionalSummary.top_holders && institutionalSummary.top_holders.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Investor
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Shares
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Change
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {institutionalSummary.top_holders.slice(0, 5).map((holder) => (
                      <tr key={holder.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 truncate max-w-xs">
                          {holder.investor_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">
                          {holder.shares >= 1_000_000
                            ? `${(holder.shares / 1_000_000).toFixed(2)}M`
                            : holder.shares.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">
                          {formatCurrency(holder.market_value)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {holder.shares_change_pct !== null && holder.shares_change_pct !== undefined ? (
                            <span
                              className={
                                holder.shares_change_pct > 0
                                  ? 'text-green-600'
                                  : holder.shares_change_pct < 0
                                    ? 'text-red-600'
                                    : 'text-gray-500'
                              }
                            >
                              {holder.shares_change_pct > 0 ? '+' : ''}
                              {(holder.shares_change_pct * 100).toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-gray-400">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
            No institutional holdings data
          </div>
        )}
      </section>
    </div>
  )
}

export default OverviewTab
